import { useStatsigClient } from '@statsig/react-bindings'
import { ReactQuery } from 'cms-api'
import { get, map, uniq } from 'lodash'
import { type NextPageWithLayout } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  PREVIEW_PAGE_ENUM,
  PreviewCallout,
} from '@public/components/PreviewCallout'
import { PUBLIC_LAYOUT_SIDE_PADDING } from '@public/components/PublicLayout'
import { Image, IMAGE_LAYOUT } from 'components/Image'
import { PLACEHOLDER } from 'components/Image/Image'
import { Box, Container, Flex, Grid } from 'components/Layout'
import { ProductImageGallery } from 'components/ProductImageGallery'
import { Seo } from 'components/Seo'
import { ASPECT_RATIO } from 'components/UploadImage/constants'
import { COMMISSION_TYPES, HEADER_HEIGHT_VALUES } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { useExpertShowroomCommission, useModal } from 'hooks'
import { type CartContextInterface, useCart } from 'providers/cart'
import {
  GA4_ECOM_CUSTOM_EVENTS,
  GA4_EVENTS,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { STATSIG_EVENTS } from 'providers/statsig'
import { LIST_ENTITY_TYPE, useItemListStore } from 'providers/useItemListStore'
import { useUserAuth } from 'providers/userAuth'
import { DEVICE, PAGE_MAX_WIDTH, SPACE } from 'Theme'
import {
  ProductVariantStatus,
  type PublicProductQuery,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import {
  SHOWROOM_COLLECTION_SLUG_REGEX,
  formatProductDetailPageMetaTitle,
  formatProductDetailPageDescription,
} from 'utils/formatters'
import { formatProductDetailTitle } from 'utils/formatTitle'
import { multiply } from 'utils/money'
import {
  MightAlsoLikeProductsCarousel,
  ProductAskExpert,
  ProductCustom,
  ProductHeader,
  RecommendedProductsCarousel,
} from './components'
import {
  getVariantsLeadTime,
  getVariantsQuantityUnit,
  pushOption,
} from './utils'

const CartModal = dynamic(() => import('../../Cart/CartModal'), { ssr: false })

const PRODUCT_HEADER_OFFSET = rem(HEADER_HEIGHT_VALUES.DESKTOP + 20)

const COLUMN_COUNT = 7
const GALLERY_COLUMN_SIZE = 4
const GALLERY_COLUMN_RATIO = GALLERY_COLUMN_SIZE / COLUMN_COUNT
const PRODUCT_DETAIL_GRID_GAP = SPACE.PX_40
const VIEW_OR_EDIT_CART = 'View or edit cart'
const CHECKOUT_NOW = 'Checkout now'

const DEFAULT_IMAGE_SIZE = rem(32)

const EMPTY_ARRAY = []

/**
 * Formula for calculating height of gallery thumbnail swiper, must use css calc sytnax
 * `(page - page padding - main grid gap) * gallery column ratio`
 */
const THUMBNAIL_SWIPER_HEIGHT_FORMULA = {
  MOBILE: `(100vw - ${SPACE.PX_30} - ${PRODUCT_DETAIL_GRID_GAP}) * ${GALLERY_COLUMN_RATIO}`,
  DESKTOP: `(${PAGE_MAX_WIDTH.LAYOUT} - ${SPACE.PX_30} - ${PRODUCT_DETAIL_GRID_GAP}) * ${GALLERY_COLUMN_RATIO}`,
  ratio: GALLERY_COLUMN_RATIO,
}

const StyledContainer = styled(Container)`
  @media screen and (max-width: ${DEVICE.TABLET}) and (min-width: ${PAGE_MAX_WIDTH.PUBLIC_LAYOUT_MOBILE}) {
    padding: 0 ${PUBLIC_LAYOUT_SIDE_PADDING};
  }
`
interface ProductDetailProps {
  productId: number
  product: PublicProductQuery['product']
  isLoading: boolean
  isPreview?: boolean
  selectedVariant: PublicProductQuery['product']['variants'][0]
  sortedVariants: PublicProductQuery['product']['variants']
}

export const ProductDetailPage: NextPageWithLayout<ProductDetailProps> = ({
  productId,
  product,
  isPreview,
  isLoading,
  selectedVariant,
  sortedVariants,
}) => {
  const lastVariantId = useRef(null)
  const detailsRef = useRef(null)
  const collectionOrExpertSlug = useRef(null)
  const [quantityAddedToCart, setQuantityAddedToCart] = useState(1)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const { getItemListData, removeItemListData } = useItemListStore()
  const router = useRouter()
  const { client: statsigClient } = useStatsigClient()
  const { MOBILE: isMobile } = useMedia()
  const { isOpen, openModal, closeModal } = useModal()
  const { isPageLoading, user, isClientSignedIn, isExpertSignedIn } =
    useUserAuth()
  const { expertCommissions, removeExpertCommision } =
    useExpertShowroomCommission()
  const postRecentlyViewedProduct =
    ReactQuery.usePostRecentlyViewedUserProductHistory()

  const isTradeUser = user?.isTradeCapable
  const {
    cart,
    setItem,
    isSetItemToCartLoading,
    getCartItemByProductVariantId,
  } = useCart()
  const { query, isReady } = router
  const openCartModal = ({ addedQuantity }) => {
    setQuantityAddedToCart(addedQuantity)
    openModal()
  }

  let commissionType = query.type as string
  const markedForCommission = expertCommissions.find(
    ({ p, e }) => productId === p && e.getTime() > new Date().getTime()
  )

  if (!commissionType) {
    commissionType = markedForCommission
      ? COMMISSION_TYPES.EXPERT_SHOWROOM_IMPROVED_ATTRIBUTION
      : commissionType
  }

  useEffect(() => {
    if (!collectionOrExpertSlug.current) {
      const match = SHOWROOM_COLLECTION_SLUG_REGEX.exec(window.location.href)
      if (match) {
        const [, slug] = match
        collectionOrExpertSlug.current = slug
      }
    }
  }, [collectionOrExpertSlug])

  const legacyProductId = product.id
  const isUserWithRecentlyViewedProductsSignedIn =
    isClientSignedIn || isExpertSignedIn

  useEffect(() => {
    if (isUserWithRecentlyViewedProductsSignedIn) {
      postRecentlyViewedProduct.mutateAsync({
        data: {
          legacyProductId,
        },
      })
    }
  }, [legacyProductId, isUserWithRecentlyViewedProductsSignedIn])

  const onSetItemHandler: CartContextInterface['setItem'] = (setItemData) => {
    const itemListData = getItemListData({
      entityId: product.id,
      entityType: LIST_ENTITY_TYPE.PRODUCT,
    })
    // Unset item we actually pushed to Cart
    removeItemListData({
      entityId: product.id,
      entityType: LIST_ENTITY_TYPE.PRODUCT,
    })

    const isImprovedAttribution =
      commissionType === COMMISSION_TYPES.EXPERT_SHOWROOM_IMPROVED_ATTRIBUTION

    if (isImprovedAttribution) {
      removeExpertCommision(productId)
    }

    return setItem({
      ...setItemData,
      itemListData,
      expertSlug: isImprovedAttribution
        ? markedForCommission.s
        : (collectionOrExpertSlug.current as string),
      type: commissionType,
    })
  }

  // reset hook called when you leave PDP page to unset localStorage
  useEffect(
    () => () => {
      removeItemListData({
        entityId: product.id,
        entityType: LIST_ENTITY_TYPE.PRODUCT,
      })
    },
    [product, removeItemListData]
  )

  useEffect(() => {
    if (selectedVariant.id !== lastVariantId.current && isReady) {
      executeDataToDataLayer({
        event: GA4_EVENTS.VIEW_ITEM,
        overrideData: {
          items: [selectedVariant],
        },
      })
      executeDataToDataLayer({
        event: GA4_ECOM_CUSTOM_EVENTS.VIEW_ITEM_SHOWROOM,
        includeStoredData: false,
      })
      if (statsigClient) {
        statsigClient.logEvent(STATSIG_EVENTS.VISIT_PDP, selectedVariant.sku, {
          isTradeCapable: user?.isTradeCapable ? 'true' : 'false',
        })
      }
      lastVariantId.current = selectedVariant.id
    }
  }, [executeDataToDataLayer, product, selectedVariant, isReady])

  const { managedCustomFields } = selectedVariant
  const quantityUnit = getVariantsQuantityUnit({
    managedCustomFields,
  })
  const cartSummary = get(cart, 'summary', null)
  const { cartQuantity } = getCartItemByProductVariantId(selectedVariant.id)
  const leadTime = getVariantsLeadTime({
    managedCustomFields,
  })
  const askExpertData = get(
    selectedVariant,
    ['customContent', 'askExpert'],
    null
  )

  const sortedPublishedVariants = sortedVariants.filter((sortedVariant) =>
    isPreview
      ? sortedVariant.status === ProductVariantStatus.Published ||
        sortedVariant.status === ProductVariantStatus.PendingApproval ||
        sortedVariant.status === ProductVariantStatus.NotPublished
      : sortedVariant.status === ProductVariantStatus.Published
  )

  const ProductHeaderComponent = (
    <ProductHeader
      product={product}
      detailsRef={detailsRef}
      cartQuantity={cartQuantity}
      openCartModal={openCartModal}
      isSetItemToCartLoading={isSetItemToCartLoading}
      setItem={onSetItemHandler}
      isLoading={isLoading || isPageLoading}
      selectedVariant={selectedVariant}
      variants={sortedPublishedVariants}
      cartId={get(cart, 'id', null)}
      onVariantChange={(optionName, value, isUnavailable) =>
        pushOption(router, {
          optionName,
          value,
          isUnavailable,
          variants: sortedPublishedVariants,
          selectedVariant,
          isPreview,
        })
      }
      isPreview={isPreview}
    />
  )

  const imagesForGallery = selectedVariant.images.length
    ? selectedVariant.images
    : product.images

  const areVariantTitlesUnique =
    uniq(map(product.variants, 'title')).length === product.variants.length

  const seoDescription =
    selectedVariant?.customContent?.accordionDetails ||
    product?.customContent?.accordionDetails ||
    product.description

  return (
    <>
      <Seo
        title={formatProductDetailTitle(
          selectedVariant.title ?? product.title,
          areVariantTitlesUnique
            ? EMPTY_ARRAY
            : selectedVariant.optionValues.map(({ value }) => value)
        )}
        metaTitle={formatProductDetailPageMetaTitle({
          title: selectedVariant.title,
          brand: product.brand?.id,
          optionValues: selectedVariant.optionValues,
        })}
        image={
          selectedVariant.images?.length
            ? selectedVariant.images[0]?.url
            : product.images[0]?.url
        }
        urlPath={ROUTE.SHOWROOM_PRODUCT_DETAIL({
          slug: product.slug,
          variantSlug: selectedVariant.slug,
        })}
        publisher={product?.brand?.id}
        description={formatProductDetailPageDescription({
          title: selectedVariant.title,
          description: seoDescription,
          brand: product.brand?.id,
          optionValues: selectedVariant.optionValues,
        })}
        canonicalUrl={ROUTE.SHOWROOM_PRODUCT_DETAIL({
          slug: product.slug,
          variantSlug: selectedVariant.slug,
        })}
      />
      {isPreview && (
        <PreviewCallout
          previewOption={PREVIEW_PAGE_ENUM.SHOWROOM_PRODUCT_PREVIEW}
          backButtonHref={ROUTE.ADMIN_PRODUCT_DETAIL({ id: productId })}
        />
      )}
      <StyledContainer>
        <Grid
          gridTemplateColumns={{
            MOBILE: '1fr',
            TABLET: `${GALLERY_COLUMN_SIZE}fr ${
              COLUMN_COUNT - GALLERY_COLUMN_SIZE
            }fr`,
          }}
          gridGap={PRODUCT_DETAIL_GRID_GAP}
          mb={{ MOBILE: SPACE.PX_48, TABLET: SPACE.PX_64 }}
          alignItems="flex-start"
        >
          <Grid position="sticky" top={PRODUCT_HEADER_OFFSET}>
            {imagesForGallery.length > 0 ? (
              <ProductImageGallery
                images={imagesForGallery}
                thumbnailSwiperHeightFormula={THUMBNAIL_SWIPER_HEIGHT_FORMULA}
              />
            ) : (
              // Default fallback image
              <Image
                aspectRatio={ASPECT_RATIO.PRODUCTS}
                alt="Default product image"
                layout={IMAGE_LAYOUT.RESPONSIVE}
                objectFit="cover"
                sizes={DEFAULT_IMAGE_SIZE}
                src={PLACEHOLDER[ASPECT_RATIO.PRODUCTS]}
              />
            )}
            {isMobile && ProductHeaderComponent}
            <ProductAskExpert customContent={selectedVariant.customContent} />
            <RecommendedProductsCarousel
              recommendedProducts={product.recommendedProducts}
              isTitleVisible={!askExpertData}
              commissionedExpertSlug={collectionOrExpertSlug.current as string}
            />
            {isMobile && (
              <ProductCustom customContent={selectedVariant.customContent} />
            )}
          </Grid>
          {!isMobile && (
            <Box position="relative">
              <Flex flexDirection="column">{ProductHeaderComponent}</Flex>
            </Box>
          )}
        </Grid>
      </StyledContainer>
      <MightAlsoLikeProductsCarousel
        products={get(product, 'mightAlsoLikeProducts', [])}
      />

      <CartModal
        ariaLabel={`${product.title} added to cart`}
        title={product.title}
        price={{
          total: multiply(selectedVariant.price.total, quantityAddedToCart),
          currencyIsoCode: selectedVariant.price.currencyIsoCode,
        }}
        tradePrice={{
          total: multiply(
            selectedVariant.tradePrice.total,
            quantityAddedToCart
          ),
          currencyIsoCode: selectedVariant.tradePrice.currencyIsoCode,
        }}
        optionValues={selectedVariant.optionValues}
        cartQuantity={quantityAddedToCart}
        quantityUnit={quantityUnit}
        imageSrc={selectedVariant.images[0]?.url}
        imageAlt={selectedVariant.images[0]?.alt || product.previewImageAlt}
        isOpen={isOpen && !!cart}
        onModalClose={closeModal}
        cartItemsCount={cartSummary?.itemsCount}
        cartTotalPrice={cartSummary?.totalPrice}
        leftButtonText={VIEW_OR_EDIT_CART}
        rightButtonText={CHECKOUT_NOW}
        onRightButtonClick={() => {
          executeDataToDataLayer({
            event: GA4_EVENTS.CLICK,
            overrideData: {
              text: CHECKOUT_NOW,
              url: ROUTE.SHOWROOM_CHECKOUT_CONTACT(),
              name: 'Checkout now button in add to cart popup',
            },
          })
          router.push(ROUTE.SHOWROOM_CHECKOUT_CONTACT())
        }}
        onLeftButtonClick={() => {
          executeDataToDataLayer({
            event: GA4_EVENTS.CLICK,
            overrideData: {
              text: VIEW_OR_EDIT_CART,
              url: ROUTE.SHOWROOM_CART(),
              name: 'View or edit cart button in add to cart popup',
            },
          })
          router.push(ROUTE.SHOWROOM_CART())
        }}
        isLoading={isLoading}
        leadTime={leadTime}
        isTradeDiscountAvailable={isTradeUser}
      />
    </>
  )
}
